import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  Type,
  Table,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableThumbnail,
} from '@spotify-internal/encore-creator-web';
import { take } from 'lodash';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';
import { commaFormatNumber } from '../../../lib/Formatter';

import {
  LOADING,
  ERROR,
  selectNetworkTopEpisodes,
  selectNetworkTopEpisodesStatus,
} from '../../../store/selectors';
import { loadTopEpisodes } from '../../../store/networkMetadata';
import { TopEpisode, ReduxState } from '../../../types';

type Props = {
  networkId: string;
  topEpisodes?: TopEpisode[];
  status?: 'LOADING' | 'ERROR';
  loadTopEpisodes: typeof loadTopEpisodes;
} & RouteComponentProps;

export class NetworkTopEpisodes extends PureComponent<Props> {
  componentDidMount() {
    this.props.loadTopEpisodes(this.props.networkId);
  }

  renderTableRow(episode: TopEpisode) {
    return (
      <TableRow hover key={episode.id} data-testid="table-row">
        <TableCell truncate title={episode.name}>
          <Link to={`/episode/${episode.id}`} style={{ textDecoration: 'none' }}>
            <TableThumbnail
              small
              img={episode.artworkUrl}
              imgAlt={episode.name}
              thumbnailTitle={episode.name}
            />
          </Link>
        </TableCell>
        <TableCell align="right" numerical>
          {commaFormatNumber(episode.starts)}
        </TableCell>
      </TableRow>
    );
  }

  render() {
    if (this.props.status === LOADING) {
      return <span data-qa="network-top-episodes-loading" />;
    }

    if (this.props.status === ERROR) {
      return <span data-qa="network-top-episodes-error" />;
    }

    if (!this.props.topEpisodes) {
      return null;
    }

    return (
      <div style={{ width: '45%' }}>
        <Type as="h1" variant={Type.heading3} condensed>
          Top episodes
        </Type>
        <Type as="p" variant={Type.cta3} color="gray60" style={{ fontSize: '10px' }}>
          Last 7 Days
        </Type>

        <Table data-qa="network-top-episodes-table">
          <colgroup>
            <col />
            <col />
          </colgroup>
          <thead>
            <TableRow>
              <TableHeaderCell>title</TableHeaderCell>
              <TableHeaderCell align="right">starts</TableHeaderCell>
            </TableRow>
          </thead>
          <tbody>
            {take(this.props.topEpisodes, 10).map(episode => this.renderTableRow(episode))}
          </tbody>
        </Table>
      </div>
    );
  }
}

const selectors = createStructuredSelector<
  ReduxState,
  {
    topEpisodes: ReturnType<typeof selectNetworkTopEpisodes>;
    status: ReturnType<typeof selectNetworkTopEpisodesStatus>;
  }
>({
  topEpisodes: selectNetworkTopEpisodes,
  status: selectNetworkTopEpisodesStatus,
});

const actions = {
  loadTopEpisodes,
};

export const NetworkTopEpisodesWithRouter = withRouter(
  connect(selectors, actions)(NetworkTopEpisodes),
);
